/** @jsxImportSource @emotion/react */
import React from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'

/**
 * Props for the PlayerStep component
 * @typedef {Object} PlayerStepProps
 * @property {Object} data - The invitation data
 * @property {Function} onNext - Function to call when proceeding to next step
 * @property {Array<string>} fieldErrors - Array of field names with errors
 * @property {Object} customError - Custom error messages by field name
 * @property {string} playerName - Player's first name
 * @property {string} playerLastName - Player's last name
 * @property {string} playerDOB - Player's date of birth
 * @property {string} playerHeightFT - Player's height in feet
 * @property {string} playerHeightIN - Player's height in inches
 * @property {string} playerWeight - Player's weight
 * @property {Function} setPlayerName - Function to set player's first name
 * @property {Function} setPlayerLastName - Function to set player's last name
 * @property {Function} setPlayerDOB - Function to set player's date of birth
 * @property {Function} setPlayerHeightFT - Function to set player's height in feet
 * @property {Function} setPlayerHeightIN - Function to set player's height in inches
 * @property {Function} setPlayerWeight - Function to set player's weight
 * @property {Function} validateForm - Function to validate the form
 * @property {Function} onBack - Function to go back to previous step
 */
type PlayerStepProps = {
  data: any
  onNext: () => void
  fieldErrors?: string[]
  customError?: Record<string, string>
  playerName: string
  playerLastName: string
  playerDOB: string
  playerHeightFT: string
  playerHeightIN: string
  playerWeight: string
  setPlayerName: (value: string) => void
  setPlayerLastName: (value: string) => void
  setPlayerDOB: (value: string) => void
  setPlayerHeightFT: (value: string) => void
  setPlayerHeightIN: (value: string) => void
  setPlayerWeight: (value: string) => void
  validateForm: () => Promise<boolean>
  onBack: () => void
  stepState: number[]
  /** Hide the welcome title + "Let's Review" header — used inside a
   *  card that already carries the invite header. Defaults to showing it. */
  hideHeader?: boolean
}

/**
 * PlayerStep component for the underage invitation flow
 * Allows user to enter or confirm player information
 *
 * @param {PlayerStepProps} props - Component props
 * @returns {JSX.Element} The rendered component
 */
/*
 * Centers the header inside the underage flow. In InviteV2 (hideHeader) the
 * header is suppressed entirely, so it renders a fragment rather than adding
 * an empty flex item to that card.
 */
const HeaderWrap = ({
  centered,
  children,
}: {
  centered: boolean
  children: React.ReactNode
}) => (centered ? <div css={headerStyle}>{children}</div> : <>{children}</>)

const PlayerStep = ({
  data,
  onNext,
  fieldErrors = [],
  customError = {},
  playerName,
  playerLastName,
  playerDOB,
  playerHeightFT,
  playerHeightIN,
  playerWeight,
  setPlayerName,
  setPlayerLastName,
  setPlayerDOB,
  setPlayerHeightFT,
  setPlayerHeightIN,
  setPlayerWeight,
  onBack,
  stepState,
  hideHeader = false,
}: PlayerStepProps) => {
  return (
    <div css={containerStyle}>
      <HeaderWrap centered={!hideHeader}>
        {!hideHeader && (
          <Text variant='heading2' weight='semiBold' color={colors.NEUTRAL_0}>
            {capitalizeWords(`Welcome To ${t('common:appName', {})}`)}
          </Text>
        )}
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_0}
        >
          {"Let's Review "}
          <span
            css={css`
              font-weight: 700;
            `}
          >
            {`${data?.name_first ?? ''}'s Profile`}
          </span>
          {':'}
        </Text>
      </HeaderWrap>

      <form css={formStyle}>
        <div css={rowStyle}>
          <div css={halfColumnStyle}>
            <TextInput
              name='player.name_first'
              customError={customError}
              fieldErrors={fieldErrors}
              label='First Name *'
              setValue={(value) => setPlayerName(value)}
              value={playerName}
              placeholder='Player First Name'
            />
          </div>
          <div css={halfColumnStyle}>
            <TextInput
              name='player.name_last'
              customError={customError}
              fieldErrors={fieldErrors}
              label='Last Name *'
              setValue={(value) => setPlayerLastName(value)}
              value={playerLastName}
              placeholder='Player Last Name'
            />
          </div>
        </div>

        <div css={rowStyle}>
          <div css={fullColumnStyle}>
            <DateOfBirthInput
              name='player.birth_date'
              value={playerDOB}
              setValue={(value) => setPlayerDOB(value)}
              label='Birth Date *'
              fieldErrors={fieldErrors}
              customError={customError}
            />
          </div>
        </div>
        <div css={rowStyle}>
          {/* Height is one field expressed in two units, so a single label
              spans both inputs and the ft/in suffixes tell them apart. */}
          <div css={halfColumnStyle}>
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.WHITE}
            >
              Height
            </Text>
            <div css={heightPairStyle}>
              <div css={heightInputStyle}>
                <TextInput
                  name='player.height'
                  customError={customError}
                  fieldErrors={fieldErrors}
                  label=''
                  setValue={(value) => setPlayerHeightFT(value)}
                  value={playerHeightFT}
                  placeholder='Height'
                  type='number'
                  min={0}
                  addonAfter='ft'
                />
              </div>
              <div css={heightInputStyle}>
                <TextInput
                  name='player.height_in'
                  customError={customError}
                  fieldErrors={fieldErrors}
                  label=''
                  setValue={(value) => setPlayerHeightIN(value)}
                  value={playerHeightIN}
                  placeholder='Height'
                  type='number'
                  min={0}
                  addonAfter='in'
                />
              </div>
            </div>
          </div>

          <div css={halfColumnStyle}>
            <TextInput
              name='player.weight'
              customError={customError}
              fieldErrors={fieldErrors}
              label='Weight'
              setValue={(value) => setPlayerWeight(value)}
              value={playerWeight}
              placeholder='Weight'
              type='number'
              min={0}
              addonAfter='lbs'
            />
          </div>
        </div>
      </form>
      <div css={hideHeader ? legacyFooterStyle : footerStyle}>
        {stepState.length > 0 && (
          <div css={hideHeader ? legacyActionColumnStyle : actionColumnStyle}>
            <Button text='Back' variant='secondary' onClick={onBack} />
          </div>
        )}
        <div css={hideHeader ? legacyActionColumnStyle : actionColumnStyle}>
          <Button text='Next' onClick={onNext} variant='primary' />
        </div>
      </div>
    </div>
  )
}

export default PlayerStep

// ---- styled ----
/*
 * Only the header centers. The form fields below stay left-aligned. In
 * InviteV2 (hideHeader) this wrapper is skipped entirely so that card's
 * original layout is untouched.
 */
const headerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  width: 100%;
`

/*
 * The two actions split the column evenly, so a step with only Next fills the
 * same footprint as one with Back + Next.
 */
const footerStyle = css`
  display: flex;
  gap: 8px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`

const actionColumnStyle = css`
  display: flex;
  flex: 1;
  min-width: 0;
`

// InviteV2 renders this step inside its own card; these preserve that surface's
// original right-aligned footer.
const legacyFooterStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  align-self: flex-end;
  gap: 16px;
  width: 100%;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column-reverse;
  }
`

const legacyActionColumnStyle = css`
  display: flex;
  width: 50%;
  align-self: flex-end;
  @media (max-width: 768px) {
    width: 100%;
  }
`

/*
 * The centered shell (underage flow) and InviteV2's card both supply this
 * step's own padding, so the container only stacks its children.
 */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

// The form owns the vertical rhythm; rows carry no margins of their own.
const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const fullColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const rowStyle = css`
  display: flex;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const halfColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

// The two height inputs share their column evenly.
const heightPairStyle = css`
  display: flex;
  gap: 8px;
`

/*
 * TextInput renders a fragment (label, control, error), so each one needs its
 * own wrapper to become a flex item. The empty label still emits a node, which
 * `:first-of-type` hides so the controls sit directly under the shared label.
 */
const heightInputStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  /* These two carry no label of their own — the shared "Height" above the
     pair names them — so drop the empty label node TextInput still emits. */
  > p:first-of-type {
    display: none;
  }
`
